import { IncentiveLifecycleStatus } from '@varo/graphql-schema-types';
import { useLocalSearchParams, usePathname } from 'expo-router';
import { useIsFocused } from 'expo-router/react-navigation';
import {
  getIncentiveAnalyticsProperties,
  getIncentiveSubCategory,
  INCENTIVE_COMPONENT_TYPES,
  IncentiveAnalyticsContext,
  IncentiveComponentName,
  IncentiveErrorStep,
  IncentiveScreenName,
} from 'features/incentive/helpers/incentiveAnalytics';
import { useEffect } from 'react';
import {
  INCENTIVE_ERROR_ENCOUNTERED,
  INCENTIVE_OPT_IN_COMPLETED,
  INCENTIVE_OPT_IN_SUBMITTED,
} from 'shared/analytics/events/incentive';
import { VaroCategories } from 'shared/analytics/types/categories';
import {
  ComponentViewedProperties,
  ScreenViewedProperties,
} from 'shared/analytics/types/componentEvents';
import { VaroEvent } from 'shared/analytics/types/events';
import { useAnalytics } from 'shared/hooks/analytics/useAnalytics';
import { useComponentAnalytics } from 'shared/hooks/analytics/useComponentAnalytics';
import useEvent from 'shared/hooks/useEvent/useEvent';

type IncentiveAnalyticsProperties = ReturnType<
  typeof getIncentiveAnalyticsProperties
>;

interface IncentiveErrorDetails {
  errorStep: IncentiveErrorStep;
  errorCode?: string;
}

const useIncentiveContext = (
  context: IncentiveAnalyticsContext,
): IncentiveAnalyticsContext => {
  const { programId } = useLocalSearchParams<{ programId?: string }>();
  return {
    ...context,
    incentiveProgramId: context.incentiveProgramId ?? programId,
  };
};

export const useIncentiveAnalytics = (context: IncentiveAnalyticsContext) => {
  const { logEvent } = useAnalytics();
  const { logComponentViewed } = useComponentAnalytics();
  const resolvedContext = useIncentiveContext(context);
  const subCategory = getIncentiveSubCategory(resolvedContext.incentiveType);

  const logIncentiveEvent = (
    event: VaroEvent,
    properties: Record<string, unknown> = {},
  ) => {
    void logEvent(event, {
      subcategory: subCategory,
      ...getIncentiveAnalyticsProperties(resolvedContext),
      ...properties,
    });
  };

  const logOptInSubmitted = () => {
    logIncentiveEvent(INCENTIVE_OPT_IN_SUBMITTED);
  };

  const logOptInCompleted = (lifecycleStatus: IncentiveLifecycleStatus) => {
    logIncentiveEvent(INCENTIVE_OPT_IN_COMPLETED, {
      lifecycle_status: lifecycleStatus.toLowerCase(),
    });
  };

  const logErrorEncountered = ({
    errorStep,
    errorCode,
  }: IncentiveErrorDetails) => {
    logIncentiveEvent(INCENTIVE_ERROR_ENCOUNTERED, {
      error_type: errorCode ? 'system' : 'network',
      error_step: errorStep,
      error_code: errorCode,
    });
  };

  const logIncentiveComponentViewed = (
    componentName: IncentiveComponentName,
  ) => {
    const properties: ComponentViewedProperties<VaroCategories.INCENTIVE> &
      IncentiveAnalyticsProperties = {
      category: VaroCategories.INCENTIVE,
      sub_category: subCategory,
      component_type: INCENTIVE_COMPONENT_TYPES[componentName],
      component_name: componentName,
      ...getIncentiveAnalyticsProperties(resolvedContext),
    };
    logComponentViewed(properties);
  };

  return {
    logOptInSubmitted,
    logOptInCompleted,
    logErrorEncountered,
    logComponentViewed: logIncentiveComponentViewed,
  };
};

interface IncentiveScreenViewedOptions extends IncentiveAnalyticsContext {
  screenName: IncentiveScreenName;
  isVisible?: boolean;
}

export const useIncentiveScreenViewed = ({
  screenName,
  isVisible = true,
  ...context
}: IncentiveScreenViewedOptions) => {
  const { logScreenViewed } = useComponentAnalytics();
  const screenUrl = usePathname();
  const resolvedContext = useIncentiveContext(context);

  const logIncentiveScreenViewed = useEvent(() => {
    const properties: ScreenViewedProperties<VaroCategories.INCENTIVE> &
      IncentiveAnalyticsProperties = {
      category: VaroCategories.INCENTIVE,
      sub_category: getIncentiveSubCategory(resolvedContext.incentiveType),
      screen_name: screenName,
      screen_url: screenUrl,
      ...getIncentiveAnalyticsProperties(resolvedContext),
    };
    logScreenViewed(properties);
  });

  const isFocused = useIsFocused();
  const isViewed = isFocused && isVisible;

  useEffect(() => {
    if (isViewed) {
      logIncentiveScreenViewed();
    }
  }, [isViewed, logIncentiveScreenViewed]);
};
